import { useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { DownloadSimple } from '@phosphor-icons/react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import theme from '../../theme';
import { useAgents } from '../../hooks/useAgents';
import AgentCallsMetrics from '../../components/Calls/AgentCallsMetrics';
import { CallsPageSkeleton } from '../../components/Calls/Skeletons/CallsPageSkeleton';
import CallsData from '../../components/Calls/CallsData';
import { useMutation } from '@tanstack/react-query';
import { downloadCSV } from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../../components/common/MessagePopup';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import { useAppDispatch, useAppSelector } from '../../hooks/useReduxTypeScript';
import {
  setSelectedAgent,
  setFromDate,
  setToDate,
  resetCallsPageState,
} from '../../utils/redux/slices/callsPageSlice';

const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const [month, day, year] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export default function CallsPage() {
  const dispatch = useAppDispatch();
  const { selectedAgent, fromDate, toDate } = useAppSelector(
    state => state.callsPage
  );

  const { agents, isLoading: isAgentsLoading } = useAgents();
  const { alert, setAlert, showAlert } = useAlert();

  useEffect(() => {
    if (agents?.agents?.length > 0) {
      const validAgents = agents.agents.filter(
        (agent: any) =>
          agent.agentName !== '' && agent.agentChannel !== 'whatsapp'
      );

      if (validAgents.length > 0) {
        // If no agent selected in Redux, set the first valid one
        if (!selectedAgent) {
          // Check localStorage for backwards compatibility
          const savedAgent = localStorage.getItem('selectedAgent');
          const parsedAgent = savedAgent ? JSON.parse(savedAgent) : null;

          if (
            parsedAgent &&
            validAgents.some((agent: any) => agent._id === parsedAgent._id)
          ) {
            dispatch(setSelectedAgent(parsedAgent));
          } else {
            dispatch(setSelectedAgent(validAgents[0]));
          }
          // Clear localStorage as we're now using Redux
          localStorage.removeItem('selectedAgent');
        } else {
          // Ensure selected agent still exists in the agents list
          const agentStillExists = validAgents.some(
            (agent: any) => agent._id === selectedAgent._id
          );
          if (!agentStillExists) {
            dispatch(setSelectedAgent(validAgents[0]));
          }
        }
      }
    }
  }, [agents, selectedAgent, dispatch]);

  const handleAgentChange = (event: SelectChangeEvent) => {
    const selectedAgentData = agents.agents.find(
      (agent: any) => agent.agentName === event.target.value
    );
    dispatch(setSelectedAgent(selectedAgentData));
  };

  const { mutate: downloadCSVFile, isPending: isDownloading } = useMutation({
    mutationFn: downloadCSV,
    onSuccess: response => {
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `calls-${fromDate}-to-${toDate}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showAlert('CSV file downloaded successfully', 'success');
    },
    onError: (err: any) => {
      showAlert(
        err?.response?.data?.message || 'Failed to download CSV',
        'error'
      );
    },
  });

  const handleDownloadCSV = () => {
    downloadCSVFile({
      agentId: selectedAgent?._id,
      fromDate: fromDate,
      toDate: toDate,
    });
  };

  const handleFromDateChange = (newValue: Date | null) => {
    dispatch(setFromDate(formatDate(newValue)));
  };

  const handleToDateChange = (newValue: Date | null) => {
    dispatch(setToDate(formatDate(newValue)));
  };

  const handleResetFilters = () => {
    dispatch(resetCallsPageState());
  };

  if (isAgentsLoading && !selectedAgent) {
    return <CallsPageSkeleton />;
  }

  return (
    <LayoutWithSidebar margin={2}>
      <Box
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <HeaderContainer
          sx={{
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{ fontSize: '24px', fontWeight: 600 }}
            >
              Call Logs
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                color={theme.palette.lightGray.text}
                fontSize="13px"
                fontWeight={400}
                lineHeight="normal"
                letterSpacing="-0.12px"
              >
                Select Agent
              </Typography>
              <Select
                value={selectedAgent?.agentName || ''}
                onChange={handleAgentChange}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    alignItems: 'center',
                    overflow: 'hidden',
                    background: '#F8F9FC',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px',
                  },
                  minWidth: '140px',
                }}
              >
                {agents?.agents
                  ?.filter(
                    (agent: any) =>
                      agent.agentName.trim() !== '' &&
                      agent.agentChannel !== 'whatsapp'
                  )
                  .map((agent: any) => (
                    <MenuItem
                      key={agent._id}
                      value={agent.agentName}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {agent.agentName}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={parseDate(fromDate)}
                views={['year', 'month', 'day']}
                onChange={handleFromDateChange}
                format="dd-MM-yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      '& input': {
                        fontSize: '14px',
                        color: theme.palette.lightGray.text,
                        borderRadius: '6px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />

              <DatePicker
                label="To Date"
                value={parseDate(toDate)}
                views={['year', 'month', 'day']}
                onChange={handleToDateChange}
                format="dd-MM-yyyy"
                minDate={parseDate(fromDate) || undefined}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      '& input': {
                        fontSize: '14px',
                        color: theme.palette.lightGray.text,
                        borderRadius: '6px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{ textTransform: 'none' }}
            >
              Reset Filters
            </Button>
            <Tooltip title="Download Call Logs" placement="top">
              <Button
                variant="contained"
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
                onClick={handleDownloadCSV}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <DownloadSimple size={16} />
                )}
              </Button>
            </Tooltip>
          </Box>
        </HeaderContainer>
        <AgentCallsMetrics
          agentId={selectedAgent?._id}
          fromDate={fromDate}
          toDate={toDate}
        />
        {selectedAgent && (
          <CallsData
            agentId={selectedAgent._id}
            fromDate={fromDate}
            toDate={toDate}
            showAlert={showAlert}
          />
        )}
      </Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </LayoutWithSidebar>
  );
}
